import { getSupportedLanguagesForRegion } from './regionLocale';

export interface RegionGroup {
  label: string;
  countries: string[];
}

/**
 * Serviceable countries grouped by continent/region for the choose-country-region page.
 * Only countries with a COUNTRY_STORE_CONFIG entry are listed here.
 * Non-listed countries are geo-detected to /global/.
 */
export const REGION_GROUPS: RegionGroup[] = [
  {
    label: 'North America',
    countries: [
      'US', 'PR', 'CA', 'MX', 'GT', 'HN', 'SV', 'NI', 'CR', 'PA',
      'BM', 'GL',
    ],
  },
  {
    label: 'Caribbean',
    countries: [
      'DO', 'TT', 'BS', 'BB', 'JM', 'GD', 'KY', 'AW', 'GP', 'MQ',
      'TC', 'CW', 'MF', 'BL', 'SX',
    ],
  },
  {
    label: 'South America',
    countries: [
      'BR', 'AR', 'CL', 'CO', 'PE', 'VE', 'EC', 'BO', 'PY', 'UY',
      'GY', 'SR', 'GF',
    ],
  },
  {
    label: 'Europe',
    countries: [
      'GB', 'DE', 'NL', 'FR', 'CZ', 'BE', 'ES', 'IT', 'AT', 'PL',
      'IE', 'CH', 'PT', 'SE', 'DK', 'SK', 'LT', 'RO', 'HU', 'EE',
      'CY', 'LU', 'FI', 'LV', 'GR', 'SI', 'NO', 'BG', 'HR', 'IS',
      'MT', 'UA', 'RS', 'MC', 'JE', 'AD', 'AL', 'LI',
      'BA', 'MK', 'MD', 'XK', 'ME', 'SJ', 'GI', 'IM', 'GG', 'SM',
      'AX',
    ],
  },
  {
    label: 'Middle East',
    countries: [
      'AE', 'SA', 'QA', 'KW', 'BH', 'OM', 'IL', 'TR', 'JO',
      'LB',
    ],
  },
  {
    label: 'South & Central Asia',
    countries: [
      'IN', 'LK', 'MV', 'NP', 'BD', 'KZ', 'UZ', 'KG', 'AM', 'AZ',
      'GE',
    ],
  },
  {
    label: 'East Asia',
    countries: ['JP', 'CN', 'KR', 'TW', 'HK', 'MO', 'MN'],
  },
  {
    label: 'Southeast Asia',
    countries: ['TH', 'SG', 'MY', 'PH', 'ID', 'VN', 'KH', 'BN', 'TL'],
  },
  {
    label: 'Oceania',
    countries: ['AU', 'NZ', 'FJ', 'CK', 'PF'],
  },
  {
    label: 'Africa',
    countries: [
      'ZA', 'NG', 'KE', 'GH', 'EG', 'MA', 'MU', 'CI', 'SN', 'AO',
      'BW', 'NA', 'TN', 'SC', 'ZM', 'ZW', 'RW', 'BJ', 'GN',
    ],
  },
  {
    label: 'Indian Ocean & Territories',
    countries: ['RE', 'YT', 'TF'],
  },
];

/** Native language display names for non-English languages */
export const LANGUAGE_NATIVE_NAMES: Record<string, string> = {
  ja: '日本語',
  de: 'Deutsch',
  th: 'ไทย',
  fr: 'Français',
  en: 'English',
};

/**
 * Returns the languages available for a country with their native names.
 * Single-language countries return an empty array (no selection needed).
 * Multi-language countries return [{ code, nativeName }] in the same order
 * as getSupportedLanguagesForRegion (default first, then English / extras).
 */
export function getLanguageOptions(
  countryCode: string,
): { code: string; nativeName: string }[] {
  const supported = getSupportedLanguagesForRegion(countryCode);
  if (supported.length <= 1) return [];

  return supported.map((code) => ({
    code,
    nativeName: LANGUAGE_NATIVE_NAMES[code] || code,
  }));
}
